// @ts-nocheck
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import REGEX from '../../../../const/Regex';
import { normalizeHyperlink } from '../../../../utils/Utils';

const getHyperlinkDisplayText = (value) =>
  (value ?? '').trim().replace(REGEX.HTTP_HTTPS_PREFIX_REGEX, '');

/**
 * @component HyperlinkModal
 * @description Collects hyperlink text and a safe URL, suggests display text, and allows an existing link to be removed by clearing its URL.
 * @returns {JSX.Element} The hyperlink insertion or editing dialog.
 * @example <HyperlinkModal onClose={handleClose} onSubmit={handleSubmit} linkText="" linkUrl="" editing={false} />
 */
const HyperlinkModal = ({ onClose, onSubmit, linkText, linkUrl, editing }) => {
  const [text, setText] = useState(linkText);
  const [url, setUrl] = useState(linkUrl);
  const [manuallySetText, setManuallySetText] = useState(Boolean(linkText));
  const [error, setError] = useState('');

  /**
   * @function handleTextChange
   * @description Tracks manual display-text edits so subsequent URL changes do not overwrite them.
   */
  const handleTextChange = useCallback((event) => {
    const value = event?.target?.value ?? '';
    setText(value);
    setManuallySetText(Boolean(value.trim()));
  }, []);

  /**
   * @function handleUrlChange
   * @description Updates the URL, clears validation feedback, and derives display text when it has not been manually set.
   */
  const handleUrlChange = useCallback(
    (event) => {
      const value = event?.target?.value ?? '';
      setUrl(value);
      setError('');
      if (!manuallySetText) {
        setText(getHyperlinkDisplayText(value));
      }
    },
    [manuallySetText]
  );

  /**
   * @function handleSubmit
   * @description Validates and normalizes the URL before submitting link values, using URL-derived text when display text is empty.
   */
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const href = normalizeHyperlink(url);
      if (href === null) {
        setError('Enter a valid HTTP, HTTPS, email, phone, or relative URL.');
        return;
      }
      const displayText = text.trim() ? text : getHyperlinkDisplayText(url);
      onSubmit({ linkText: displayText, linkUrl: href });
    },
    [onSubmit, text, url]
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="hyperlink-dialog-title"
    >
      <Stack component="form" onSubmit={handleSubmit}>
        <DialogTitle id="hyperlink-dialog-title">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" component="span">
              {editing ? 'Edit Hyperlink' : 'Insert Hyperlink'}
            </Typography>
            <IconButton onClick={onClose} aria-label="Close hyperlink dialog">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} gap={2}>
            <Typography variant="body4">
              <strong>Note:</strong>&nbsp;
              <i>Clear the URL to remove the hyperlink.</i>
            </Typography>
            <TextField
              placeholder="Enter text to display"
              fullWidth
              label="Text to display"
              value={text}
              onChange={handleTextChange}
            />
            <TextField
              fullWidth
              label="Link (URL)"
              value={url}
              onChange={handleUrlChange}
              error={Boolean(error)}
              placeholder="https://example.com"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!editing && !url.trim() && !text.trim()}
          >
            {editing && !url.trim() ? 'Remove link' : 'Apply'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

HyperlinkModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  linkText: PropTypes.string.isRequired,
  linkUrl: PropTypes.string.isRequired,
  editing: PropTypes.bool.isRequired,
};

export default HyperlinkModal;
